import { useState } from 'react'
import { PlayCircle, RotateCcw, Stethoscope } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useSourceMutations } from '../api'
import type { SourceRunView, SourceView, TestResult } from '../types'
import { DatePicker } from '@/components/ui/date-picker'

/**
 * Test, sync now, and replay a range — the three things somebody does while getting a
 * source working.
 *
 * Replay is separated from sync on purpose and says so. Rewinding the watermark to re-read
 * a week would make the next scheduled poll re-import everything since; a replay reads the
 * range you name and leaves the watermark alone, and the result makes that visible.
 */
export function SourceActions({ source }: { source: SourceView }) {
  const { test, poll, replay } = useSourceMutations()
  const [result, setResult] = useState<TestResult | null>(null)
  const [run, setRun] = useState<SourceRunView | null>(null)
  const [showReplay, setShowReplay] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const busy = test.isPending || poll.isPending || replay.isPending
  const pollable = source.type === 'SQL_DATABASE' || source.type === 'BIOTIME_REST'

  async function guard(work: () => Promise<void>) {
    setError(null)
    try {
      await work()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not work')
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            void guard(async () => {
              setRun(null)
              setResult(await test.mutateAsync(source.id))
            })
          }}
        >
          <Stethoscope className="size-4" aria-hidden="true" />
          Test connection
        </Button>

        {pollable && (
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                void guard(async () => {
                  setResult(null)
                  setRun(await poll.mutateAsync(source.id))
                })
              }}
            >
              <PlayCircle className="size-4" aria-hidden="true" />
              Sync now
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => setShowReplay((open) => !open)}
              aria-expanded={showReplay}
            >
              <RotateCcw className="size-4" aria-hidden="true" />
              Replay a range
            </Button>
          </>
        )}
      </div>

      {showReplay && (
        <form
          className="flex flex-wrap items-end gap-3 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3"
          onSubmit={(event) => {
            event.preventDefault()
            void guard(async () => {
              setResult(null)
              setRun(await replay.mutateAsync({ id: source.id, from, to }))
            })
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="replay-from" required>From</Label>
            <DatePicker id="replay-from" required value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="replay-to" required>To</Label>
            <DatePicker id="replay-to" required value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy}>
            {replay.isPending ? 'Reading…' : 'Replay'}
          </Button>
          <p className="w-full text-xs text-[var(--color-muted)]">
            Re-reads that range. Punches already stored are skipped, and the watermark does not
            move — so this cannot cause the next scheduled sync to re-import everything.
          </p>
        </form>
      )}

      {error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      {result && (
        <p
          role="status"
          className={result.success ? 'text-sm text-[var(--color-success)]' : 'text-sm text-[var(--color-danger)]'}
        >
          {result.message}
        </p>
      )}

      {run && (
        <p role="status" className="text-sm text-[var(--color-body)]">
          Read <strong>{run.rowsRead}</strong>, stored <strong>{run.rowsInserted}</strong> new
          {run.rowsDuplicate > 0 && <>, {run.rowsDuplicate} already had</>}
          {run.rowsUnmatched > 0 && (
            <>
              ,{' '}
              <span className="text-[var(--color-warning)]">
                {run.rowsUnmatched} matched no employee
              </span>
            </>
          )}
          .{' '}
          {run.watermarkBefore === run.watermarkAfter
            ? 'The watermark did not move.'
            : `Watermark now ${run.watermarkAfter}.`}
        </p>
      )}
    </div>
  )
}
